import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from "uuid";

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';

import {
  ModelBase,
} from "@models";
import {
  DiceRoll,
  LootEntry,
  LootItemGroup,
  LootTable,
  MaterialLootEntry,
} from "@dndModels";
import { allNamedItems, loadEditorItems } from "@dndUtilities/encounter_loot";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import LootEntryInput from "@dndComponents/other/LootEntryInput";
import MaterialEntryInput from "@dndComponents/other/MaterialEntryInput";
import LootTableImport from "@dndComponents/other/LootTableImport";
import LootPatternsTab from "@dndComponents/other/LootPatternsTab";
import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

const entries_per_page = 20;

interface Props {
  obj?: ModelBase;
}

const crRange = (min_cr: number, max_cr: number) => {
  if (min_cr < 0 && max_cr < 0) {
    return "";
  } else if (min_cr === max_cr) {
    return `CR ${min_cr}`;
  }
  return `CR ${min_cr < 0 ? "0" : min_cr} - ${max_cr < 0 ? "Any" : max_cr}`;
}

const diceString = (dice: DiceRoll) => {
  const rolled = dice.size === 1 ? `${dice.count}` : `${dice.count}d${dice.size}`;
  return dice.flat ? `${rolled}+${dice.flat}` : rolled;
}

const groupSummary = (group: LootItemGroup, item_names: Map<string, string>) => {
  const dice = diceString(group.count);
  if (group.source === "Specific Item") {
    return `${dice} x ${item_names.get(group.item_id) ?? "(item not set)"}`;
  }
  if (group.item_limit.pseudo_item_ids.length > 0) {
    return `${dice} of ${group.item_limit.pseudo_item_ids.length} item(s)`;
  }
  const value = group.min_value < 0 && group.max_value < 0 ? "" :
    ` worth ${group.min_value < 0 ? 0 : group.min_value}-${group.max_value < 0 ? "any" : group.max_value} gp`;
  return `${dice} item(s)${value}`;
}

const entrySummary = (entry: LootEntry, item_names: Map<string, string>) => {
  const pieces: string[] = [entry.guaranteed ? "Guaranteed" : `Probability Weight ${entry.weight}`];
  if (entry.pattern_id !== "") {
    pieces.unshift("From a Pattern");
  }
  if (entry.difficulties.length > 0) {
    pieces.push(entry.difficulties.join("/"));
  }
  const cr = crRange(entry.min_cr, entry.max_cr);
  if (cr !== "") {
    pieces.push(cr);
  }
  if (entry.environment_ids.length > 0) {
    pieces.push(`${entry.environment_ids.length} Environment(s)`);
  }
  if (!entry.creature_limit.isEmpty()) {
    pieces.push(`Creatures: ${entry.creature_limit.toString()}`);
  }
  pieces.push(...entry.item_groups.map(o => groupSummary(o, item_names)));
  if (!entry.money.isEmpty()) {
    pieces.push("Money");
  }
  return pieces.join(", ");
}

const materialSummary = (entry: MaterialLootEntry) => {
  const pieces: string[] = [`${entry.chance}% per creature`];
  const cr = crRange(entry.min_cr, entry.max_cr);
  if (cr !== "") {
    pieces.push(cr);
  }
  if (!entry.creature_limit.isEmpty()) {
    pieces.push(`Creatures: ${entry.creature_limit.toString()}`);
  }
  pieces.push(`${diceString(entry.amount)} ${entry.material_type} (${entry.weight} lb, ${entry.value} gp each)`);
  return pieces.join(", ");
}

const TABS = ["Coins & Items", "Patterns", "Crafting Materials"];

export default function LootTableEdit({ obj: param_obj }: Props) {
  const [obj, setObj] = useState<LootTable>(param_obj instanceof LootTable ? param_obj : api.applyWorkingOn(new LootTable()));
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState(TABS[0]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [item_options, setItemOptions] = useState<{ id: string, name: string }[] | null>(null);

  useEffect(() => {
    loadEditorItems().then(() => {
      setItemOptions(allNamedItems().sort((a, b) => a.name.localeCompare(b.name)));
    });
  }, []);

  const item_names = new Map((item_options ?? []).map(o => [o.id, o.name]));

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const updateObj = (updater: (o: LootTable) => void) => {
    const new_obj = obj.clone(); // clone, don't mutate
    updater(new_obj);
    setObj(new_obj);
    setChangeTime(common_util.get_time_number());
  };

  const toggleExpanded = (true_id: string) => {
    setExpanded(expanded.includes(true_id) ? expanded.filter(o => o !== true_id) : [...expanded, true_id]);
  };

  // Both tabs list their entries the same way, they just hold different kinds of entries.
  const renderEntryList = <T extends LootEntry | MaterialLootEntry>({
    entries,
    getList,
    setList,
    summary,
    renderEditor,
  }: {
    entries: T[],
    getList: (o: LootTable) => T[],
    setList: (o: LootTable, list: T[]) => void,
    summary: (entry: T) => string,
    renderEditor: (entry: T, onChange: (update: (entry: T) => void) => void) => React.ReactNode,
  }) => {
    const shown = search === "" ? entries :
      entries.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
    const page_count = Math.max(Math.ceil(shown.length / entries_per_page), 1);
    const use_page = Math.min(page, page_count - 1);
    const current_page = shown.slice(use_page * entries_per_page, (use_page + 1) * entries_per_page);

    const updateEntry = (true_id: string, update: (entry: T) => void) => {
      updateObj(o => {
        const entry = getList(o).find(e => e.true_id === true_id);
        if (entry) {
          update(entry);
        }
      });
    };

    return (
      <>
        { current_page.map(entry => {
          const is_expanded = expanded.includes(entry.true_id);
          return (
            <Grid key={entry.true_id} size={12} className="padded bordered">
              <Grid container spacing={0} direction="row">
                <Grid size={10} className="clickable" onClick={() => toggleExpanded(entry.true_id)}>
                  <div className="header">{ entry.name === "" ? "(Unnamed)" : entry.name }</div>
                  <div className="small_label gray">{ summary(entry) }</div>
                </Grid>
                <Grid size={2} className="centered">
                  <CopyButton
                    disabled={disabled}
                    onClick={() => {
                      const copy = entry.clone() as T;
                      copy.true_id = uuidv4().toString();
                      copy.name = `${entry.name} (Copy)`;
                      updateObj(o => {
                        const list = getList(o);
                        const index = list.findIndex(e => e.true_id === entry.true_id);
                        list.splice(index + 1, 0, copy);
                      });
                    }}
                  />
                  <DeleteButton
                    disabled={disabled}
                    no_fab
                    onClick={() => updateObj(o => { setList(o, getList(o).filter(e => e.true_id !== entry.true_id)); })}
                  />
                  <span className="clickable" onClick={() => toggleExpanded(entry.true_id)}>
                    { is_expanded ? <ExpandLess /> : <ExpandMore /> }
                  </span>
                </Grid>
                { is_expanded &&
                  <Grid size={12} className="padded">
                    { renderEditor(entry, update => updateEntry(entry.true_id, update)) }
                  </Grid>
                }
              </Grid>
            </Grid>
          );
        })}
        { page_count > 1 &&
          <Grid size={12}>
            <Grid container spacing={1}>
              <Grid size={2}>
                <DandDButtonBox name="Previous" disabled={use_page === 0} onClick={() => setPage(use_page - 1)} />
              </Grid>
              <Grid size={8} className="centered">
                Page { use_page + 1 } of { page_count }
              </Grid>
              <Grid size={2}>
                <DandDButtonBox name="Next" disabled={use_page + 1 >= page_count} onClick={() => setPage(use_page + 1)} />
              </Grid>
            </Grid>
          </Grid>
        }
      </>
    );
  };

  const renderSearch = (size: number) => (
    <Grid size={size}>
      <StringBox
        name="Search Entries"
        value={search}
        onBlur={(changed: string) => {
          setSearch(changed);
          setPage(0);
        }}
      />
    </Grid>
  );

  const renderItemsTab = () => (
    <>
      <Grid size={12} className="header">
        Coin & Item Entries ({ obj.entries.length })
      </Grid>
      <Grid size={12} className="small_label gray">
        Guaranteed entries that fit the encounter are each given once,
        then each roll picks one of the other entries that fit, by weight, from all of the campaign&apos;s loot tables.
      </Grid>
      { renderSearch(6) }
      <Grid size={3}>
        <LootTableImport
          item_options={item_options}
          disabled={disabled}
          onImport={(entries: LootEntry[]) => {
            updateObj(o => { o.entries.push(...entries); });
            setSearch("");
            setPage(Math.floor(obj.entries.length / entries_per_page));
          }}
        />
      </Grid>
      <Grid size={3}>
        <DandDButtonBox
          name="Add Entry"
          disabled={disabled}
          onClick={() => {
            const entry = new LootEntry();
            entry.name = `Entry ${obj.entries.length + 1}`;
            updateObj(o => { o.entries.push(entry); });
            setSearch("");
            setPage(Math.floor(obj.entries.length / entries_per_page));
            setExpanded([...expanded, entry.true_id]);
          }}
        />
      </Grid>
      { renderEntryList<LootEntry>({
        entries: obj.entries,
        getList: o => o.entries,
        setList: (o, list) => { o.entries = list; },
        summary: entry => entrySummary(entry, item_names),
        renderEditor: (entry, onChange) => (
          <LootEntryInput
            entry={entry}
            disabled={disabled}
            item_options={item_options ?? []}
            onChange={onChange}
          />
        ),
      }) }
    </>
  );

  const renderMaterialsTab = () => (
    <>
      <Grid size={12} className="header">
        Crafting Material Entries ({ obj.material_entries.length })
      </Grid>
      <Grid size={12} className="small_label gray">
        These are rolled separately from coins and items. After an encounter, each creature the party faced
        gets its chance at every entry that fits it, from all of the campaign&apos;s loot tables.
      </Grid>
      { renderSearch(9) }
      <Grid size={3}>
        <DandDButtonBox
          name="Add Entry"
          disabled={disabled}
          onClick={() => {
            const entry = new MaterialLootEntry();
            entry.name = `Material ${obj.material_entries.length + 1}`;
            updateObj(o => { o.material_entries.push(entry); });
            setSearch("");
            setPage(Math.floor(obj.material_entries.length / entries_per_page));
            setExpanded([...expanded, entry.true_id]);
          }}
        />
      </Grid>
      { renderEntryList<MaterialLootEntry>({
        entries: obj.material_entries,
        getList: o => o.material_entries,
        setList: (o, list) => { o.material_entries = list; },
        summary: materialSummary,
        renderEditor: (entry, onChange) => (
          <MaterialEntryInput
            entry={entry}
            disabled={disabled}
            onChange={onChange}
          />
        ),
      }) }
    </>
  );

  return (
    <ModelBaseInput
      obj={obj}
      disabled={disabled}
      change_time={change_time}
      onChange={(changed: ModelBase) => {
        if (changed instanceof LootTable) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
          setChangeTime(common_util.get_time_number());
        }
      }}>
      <Grid container spacing={1} direction="row">
        <Grid size={12}>
          <CenteredMenu
            options={TABS}
            selected={tab}
            onSelect={(changed: string) => {
              setTab(changed);
              setSearch("");
              setPage(0);
            }}
          />
        </Grid>
        { tab === "Coins & Items" && renderItemsTab() }
        { tab === "Patterns" &&
          <LootPatternsTab
            table={obj}
            disabled={disabled}
            onChange={updateObj}
          />
        }
        { tab === "Crafting Materials" && renderMaterialsTab() }
      </Grid>
    </ModelBaseInput>
  );
}
